import { getToken } from './auth'
import type {
  ApiError,
  Book,
  BookDetail,
  CreateBookRequest,
  UpdateBookRequest,
  UploadInitRequest,
  UploadInitResponse,
  RegisterAssetRequest,
  RegisterAssetResponse,
  UploadFileResponse,
  UpdateAssetResponse,
  PaginatedResponse,
  AdminUser,
  UserRole,
  HelpArticleAdmin,
  HelpArticlePayload,
  HelpArticleUpdatePayload,
  FeedbackRecord,
  FeedbackReply,
  FeedbackReplyPayload,
  FeedbackStatus,
  BatchCreateUsersResponse,
  AdminDiscussion,
  DiscussionStatus
} from '@/types'

class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string = '/api') {
    this.baseUrl = baseUrl
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = getToken()
    const url = `${this.baseUrl}${endpoint}`

    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData
    const headers = new Headers(options.headers || {})

    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    if (isFormData) {
      headers.delete('Content-Type')
    } else if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }

    const controller = new AbortController()
    // ponytail: 文件上传给 10 分钟，其余 30 秒。真要进度条再上 XHR。
    const timeoutId = setTimeout(() => controller.abort(), isFormData ? 600_000 : 30_000)

    const config: RequestInit = {
      ...options,
      headers,
      signal: controller.signal,
    }

    let response: Response
    try {
      response = await fetch(url, config)
    } catch (err) {
      clearTimeout(timeoutId)
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw { code: 'TIMEOUT', message: '请求超时，请检查网络后重试' } as ApiError
      }
      throw { code: 'NETWORK_ERROR', message: '网络连接失败，请检查网络后重试' } as ApiError
    } finally {
      clearTimeout(timeoutId)
    }
    
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        window.dispatchEvent(new CustomEvent('auth:expired'))
      }

      let error: ApiError
      try {
        error = await response.json()
      } catch {
        error = {
          code: 'NETWORK_ERROR',
          message: `HTTP ${response.status}: ${response.statusText}`,
        }
      }
      throw error
    }

    if (response.status === 204) {
      return {} as T
    }

    return response.json()
  }

  // Auth - 获取公钥
  private cachedPublicKey: string | null = null

  private async getPublicKey(): Promise<string> {
    if (this.cachedPublicKey) return this.cachedPublicKey
    const response = await this.request<{ publicKey: string }>('/auth/public-key', { method: 'GET' })
    this.cachedPublicKey = response.publicKey
    return response.publicKey
  }

  private async getNonce(): Promise<string> {
    const response = await this.request<{ nonce: string }>('/auth/nonce', { method: 'GET' })
    return response.nonce
  }

  private async encryptCredentials(password: string, nonce: string, timestamp: number, publicKeyPem: string): Promise<string> {
    // 解析 PEM 公钥
    const pemContents = publicKeyPem
      .replace(/-----BEGIN PUBLIC KEY-----/, '')
      .replace(/-----END PUBLIC KEY-----/, '')
      .replace(/\s/g, '')

    const keyData = Uint8Array.from(atob(pemContents), c => c.charCodeAt(0))

    // 导入公钥
    const publicKey = await crypto.subtle.importKey(
      'spki',
      keyData,
      { name: 'RSA-OAEP', hash: 'SHA-256' },
      false,
      ['encrypt']
    )

    // 加密数据
    const credentials = JSON.stringify({ password, nonce, timestamp })
    const encoded = new TextEncoder().encode(credentials)
    const encrypted = await crypto.subtle.encrypt(
      { name: 'RSA-OAEP' },
      publicKey,
      encoded
    )

    // 返回 Base64
    return btoa(String.fromCharCode(...new Uint8Array(encrypted)))
  }

  async login(username: string, password: string) {
    // 获取公钥和 nonce
    const publicKey = await this.getPublicKey()
    const nonce = await this.getNonce()
    const timestamp = Math.floor(Date.now() / 1000)

    // 加密凭证
    const encrypted = await this.encryptCredentials(password, nonce, timestamp, publicKey)

    return this.request<{ token: string; user?: { id: string; username: string; role: UserRole; level: number } }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, encrypted }),
    })
  }

  async logout() {
    return this.request('/auth/logout', {
      method: 'POST',
    })
  }

  // Books Management
  async getBooks(params?: {
    page?: number
    size?: number
    status?: string
    search?: string
    theme?: string
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
  }): Promise<PaginatedResponse<Book>> {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.size) searchParams.set('size', params.size.toString())
    if (params?.status) searchParams.set('status', params.status)
    if (params?.search?.trim()) searchParams.set('search', params.search.trim())
    if (params?.theme) searchParams.set('theme', params.theme)
    if (params?.sortBy) searchParams.set('sortBy', params.sortBy)
    if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder)

    const query = searchParams.toString()
    return this.request<PaginatedResponse<Book>>(
      `/admin/books${query ? `?${query}` : ''}`
    )
  }

  async getBook(id: string): Promise<BookDetail> {
    return this.request<BookDetail>(`/books/${id}`)
  }

  async createBook(data: CreateBookRequest): Promise<Book> {
    return this.request<Book>('/admin/books', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateBook(id: string, data: UpdateBookRequest): Promise<{ id: string; updated: boolean }> {
    return this.request<{ id: string; updated: boolean }>(`/admin/books/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async deleteBook(id: string): Promise<void> {
    return this.request<void>(`/admin/books/${id}`, {
      method: 'DELETE',
    })
  }

  // Asset Management
  async initUpload(bookId: string, data: UploadInitRequest): Promise<UploadInitResponse> {
    return this.request<UploadInitResponse>(`/admin/books/${bookId}/assets/init`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async uploadFile(bookId: string, file: File, format: string, r2Key: string): Promise<UploadFileResponse> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('format', format)
    formData.append('r2Key', r2Key)

    return this.request<UploadFileResponse>(`/admin/books/${bookId}/assets/upload`, {
      method: 'POST',
      headers: {}, // Remove content-type to let browser set multipart boundary
      body: formData,
    })
  }

  async registerAsset(bookId: string, data: RegisterAssetRequest): Promise<RegisterAssetResponse> {
    return this.request<RegisterAssetResponse>(`/admin/books/${bookId}/assets`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateAsset(bookId: string, assetId: string, data: {
    filename?: string
    isPrimary?: boolean
    contentType?: string
  }): Promise<UpdateAssetResponse> {
    return this.request<UpdateAssetResponse>(`/admin/books/${bookId}/assets/${assetId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async deleteAsset(bookId: string, assetId: string): Promise<void> {
    return this.request<void>(`/admin/books/${bookId}/assets/${assetId}`, {
      method: 'DELETE',
    })
  }

  async getAssetDownloadUrl(bookId: string, assetId: string): Promise<{ downloadUrl: string; filename: string }> {
    return this.request<{ downloadUrl: string; filename: string }>(
      `/admin/books/${bookId}/assets/${assetId}/download`
    )
  }

  // App release management
  async getAppDownloadConfig() {
    return this.request<{ config: import('@/types').AppDownloadConfig }>(
      '/admin/apps/download-config',
    )
  }

  async updateAppDownloadConfig(payload: {
    androidDownloadUrl?: string | null
    iosDownloadUrl?: string | null
  }) {
    return this.request<{ config: import('@/types').AppDownloadConfig }>(
      '/admin/apps/download-config',
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
    )
  }

  async getAppReleases(limit = 50) {
    const params = new URLSearchParams({ limit: String(limit) })
    return this.request<{ releases: import('@/types').AppReleaseGroup[] }>(
      `/admin/apps/releases?${params.toString()}`
    )
  }

  async createAppRelease(payload: {
    version: string
    buildNumber: number
    releaseNotes?: string
  }) {
    return this.request<{ release: import('@/types').AppReleaseGroup }>(
      '/admin/apps/releases',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    )
  }

  async updateAppReleaseGroup(
    buildNumber: number,
    payload: {
      version?: string
      buildNumber?: number
      releaseNotes?: string | null
    },
  ) {
    return this.request<{ release: import('@/types').AppReleaseGroup | null }>(
      `/admin/apps/releases/by-build/${buildNumber}`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
    )
  }

  async deleteAppReleaseGroup(buildNumber: number) {
    return this.request<void>(
      `/admin/apps/releases/by-build/${buildNumber}`,
      {
        method: 'DELETE',
      },
    )
  }

  async updateAppRelease(releaseId: string, payload: { version?: string; buildNumber?: number; releaseNotes?: string | null; externalUrl?: string | null }) {
    return this.request<{ release: import('@/types').AppRelease }>(
      `/admin/apps/releases/${releaseId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
    )
  }

  async deleteAppRelease(releaseId: string) {
    return this.request<void>(
      `/admin/apps/releases/${releaseId}`,
      {
        method: 'DELETE',
      },
    )
  }

  // Themes Management
  async getThemes(): Promise<{ themes: Array<{ id: string; name: string; description?: string; color?: string; bookCount?: number }> }> {
    return this.request<{ themes: Array<{ id: string; name: string; description?: string; color?: string; bookCount?: number }> }>('/admin/themes')
  }

  // Cover Management
  async uploadCover(bookId: string, file: File): Promise<{ id: string; coverUrl: string; message: string }> {
    const formData = new FormData()
    formData.append('file', file)

    return this.request<{ id: string; coverUrl: string; message: string }>(`/admin/books/${bookId}/cover`, {
      method: 'POST',
      body: formData,
    })
  }

  async deleteCover(bookId: string): Promise<void> {
    return this.request<void>(`/admin/books/${bookId}/cover`, {
      method: 'DELETE',
    })
  }

  // User Management
  async getUsers(): Promise<{ users: AdminUser[] }> {
    return this.request<{ users: AdminUser[] }>('/admin/users')
  }

  async createUser(data: { username: string; password: string; displayName?: string | null; role?: UserRole; level?: number }): Promise<{ user: AdminUser }> {
    return this.request<{ user: AdminUser }>('/admin/users', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async batchCreateUsers(data: {
    prefix: string
    count: number
    role?: UserRole
    level?: number
    passwordLength?: number
    startIndex?: number
  }): Promise<BatchCreateUsersResponse> {
    return this.request<BatchCreateUsersResponse>('/admin/users/batch', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateUser(id: string, data: { displayName?: string | null; role?: UserRole; password?: string; level?: number }): Promise<{ user: AdminUser }> {
    return this.request<{ user: AdminUser }>(`/admin/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async lockUser(id: string, reason?: string | null): Promise<{ user: AdminUser }> {
    const payload = reason && reason.trim().length > 0 ? { reason: reason.trim() } : {}
    return this.request<{ user: AdminUser }>(`/admin/users/${id}/lock`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  async unlockUser(id: string): Promise<{ user: AdminUser }> {
    return this.request<{ user: AdminUser }>(`/admin/users/${id}/lock`, {
      method: 'DELETE',
    })
  }

  async deleteUser(id: string): Promise<void> {
    await this.request<void>(`/admin/users/${id}`, {
      method: 'DELETE',
    })
  }

  async batchDeleteUsers(ids: string[]): Promise<{ deleted: number; requested: number; adminRemoved: number }> {
    return this.request<{ deleted: number; requested: number; adminRemoved: number }>(
      '/admin/users/batch-delete',
      {
        method: 'POST',
        body: JSON.stringify({ ids }),
      }
    )
  }

  // Help Center Management
  async getHelpArticles(): Promise<HelpArticleAdmin[]> {
    const response = await this.request<{ items: HelpArticleAdmin[] }>(
      '/admin/help/articles'
    )
    return response.items
  }

  async createHelpArticle(data: HelpArticlePayload): Promise<HelpArticleAdmin> {
    return this.request<HelpArticleAdmin>('/admin/help/articles', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateHelpArticle(
    id: string,
    data: HelpArticleUpdatePayload
  ): Promise<HelpArticleAdmin> {
    return this.request<HelpArticleAdmin>(`/admin/help/articles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async deleteHelpArticle(id: string): Promise<void> {
    return this.request<void>(`/admin/help/articles/${id}`, {
      method: 'DELETE',
    })
  }

  // Feedback Management
  async getFeedbacks(params?: {
    category?: string
    status?: FeedbackStatus
    from?: string
    to?: string
    limit?: number
  }): Promise<FeedbackRecord[]> {
    const searchParams = new URLSearchParams()
    if (params?.category) searchParams.set('category', params.category)
    if (params?.status) searchParams.set('status', params.status)
    if (params?.from) searchParams.set('from', params.from)
    if (params?.to) searchParams.set('to', params.to)
    if (params?.limit) searchParams.set('limit', params.limit.toString())

    const query = searchParams.toString()
    const response = await this.request<{ items: FeedbackRecord[] }>(
      `/admin/feedback${query ? `?${query}` : ''}`
    )
    return response.items
  }

  async replyFeedback(
    feedbackId: string,
    payload: FeedbackReplyPayload
  ): Promise<{ reply: FeedbackReply; status: FeedbackStatus }> {
    const response = await this.request<{
      id: string
      feedbackId: string
      adminId?: string | null
      adminName?: string | null
      message: string
      isPublic: boolean
      createdAt: string
      status: FeedbackStatus
    }>(`/admin/feedback/${feedbackId}/reply`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })

    const reply: FeedbackReply = {
      id: response.id,
      feedbackId: response.feedbackId,
      adminId: response.adminId,
      adminName: response.adminName,
      message: response.message,
      isPublic: response.isPublic,
      createdAt: response.createdAt,
    }

    return { reply, status: response.status }
  }

  async updateFeedbackStatus(
    feedbackId: string,
    status: FeedbackStatus
  ): Promise<{ id: string; status: FeedbackStatus }> {
    return this.request<{ id: string; status: FeedbackStatus }>(
      `/admin/feedback/${feedbackId}/status`,
      {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }
    )
  }

  // Discussion Management
  async getAdminDiscussions(params?: {
    page?: number
    size?: number
    category?: string
    status?: string
    search?: string
  }): Promise<PaginatedResponse<AdminDiscussion>> {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.size) searchParams.set('size', params.size.toString())
    if (params?.category) searchParams.set('category', params.category)
    if (params?.status) searchParams.set('status', params.status)
    if (params?.search?.trim()) searchParams.set('search', params.search.trim())

    const query = searchParams.toString()
    return this.request<PaginatedResponse<AdminDiscussion>>(
      `/admin/discussions${query ? `?${query}` : ''}`
    )
  }

  async togglePinDiscussion(id: string): Promise<{ isPinned: boolean }> {
    return this.request<{ isPinned: boolean }>(`/admin/discussions/${id}/pin`, {
      method: 'POST',
    })
  }

  async toggleTopDiscussion(id: string): Promise<{ isTop: boolean }> {
    return this.request<{ isTop: boolean }>(`/admin/discussions/${id}/top`, {
      method: 'POST',
    })
  }

  async updateDiscussionStatus(
    id: string,
    status: DiscussionStatus
  ): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/admin/discussions/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    })
  }

  async deleteDiscussion(id: string): Promise<{ success: boolean; message?: string }> {
    return this.request<{ success: boolean; message?: string }>(`/admin/discussions/${id}`, {
      method: 'DELETE',
    })
  }

}

export const api = new ApiClient()
